import React, { useState, useMemo } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import type { ViewStyle } from 'react-native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Card from 'src/presentation/components/atoms/Card';
import ArchiveItem from 'src/presentation/components/molecules/ArchiveItem';
import Title from 'src/presentation/components/atoms/Title';
import Divider from 'src/presentation/components/atoms/Divider';
import Translations from 'src/core/utils/Translations';
import _ from 'lodash';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import type { ApiEvent } from 'src/core/models/api/ApiEvent';

const ArchiveLayout = () => {
	const [filtered, setFiltered] = useState(false);
	const [filteredResults, setFilteredResults] = useState<ApiEvent[] | false>(false);
	const [refreshing, setRefreshing] = useState(false);

	const eventStore = useEventStore();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { grid: ViewStyle } => ({ grid: { width: contentWidth } }), [contentWidth]);

	const onRefresh = () => {
		setRefreshing(true);
		eventStore
			.refreshAllEvents()
			.catch(() => {
				// handled by store
			})
			.finally(() => {
				setRefreshing(false);
			});
	};

	const renderArchiveForAndroid = (events: ApiEvent[]) => {
		if (filtered) {
			return renderArchiveItems(getEventDataMinusActiveEvents());
		} else {
			const recentEvents = _.dropRight(events, events.length - 4);
			const otherEvents = _.drop(events, 4);

			return (
				<View>
					<Title text={Translations.archiveRecentEvents} />
					{renderArchiveItems(recentEvents)}
					{otherEvents.length > 0 ? (
						<View>
							<Divider />
							<Title text={Translations.archiveOtherEvents} />
							{renderArchiveItems(otherEvents)}
						</View>
					) : null}
				</View>
			);
		}
	};

	const renderArchiveItems = (events: ApiEvent[] | false) => {
		if (!events) {
			return <ActivityIndicator style={styles.loadingIndicator} />;
		}

		const eventtiles = events.map((data, index: number) => {
			return <ArchiveItem data={data} key={index} />;
		});

		return <View style={[styles.grid, sizedStyles.grid]}>{eventtiles}</View>;
	};

	const getEventDataMinusActiveEvents = (): ApiEvent[] | false => {
		const eventData = _.drop(eventStore.eventData ?? [], 4);

		if (filtered) {
			return filteredResults;
		}
		return eventData;
	};

	const searchEvent = (term: string) => {
		term.length > 0 ? setFiltered(true) : setFiltered(false);

		const eventData = _.drop(eventStore.eventData ?? [], 4);
		const filteredResultsData = eventData.filter((event) => event.name.toLowerCase().includes(term.toLowerCase()));

		setFilteredResults(filteredResultsData.length > 0 ? filteredResultsData : false);
	};

	return (
		<View style={styles.root}>
			<NavBar
				backBtn={false}
				bgcolor={Colors.primary}
				centerTitle={true}
				color={Colors.black}
				editBtn={false}
				iosPlaceholder={Translations.archiveSearchPlaceholder}
				searchBtn={true}
				title={Translations.archiveDefaultTitle}
				onSearchTermChange={(term: string) => searchEvent(term)}
			/>
			<ScrollView
				contentContainerStyle={{ flexGrow: 1 }}
				refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
				style={[styles.scroll, { flex: 1 }]}
			>
				<Card>
					{(eventStore.eventData?.length || 0) <= 4 ? (
						<EmptyState
							icon="time-outline"
							message={Translations.archiveEmptyMessage}
							title={Translations.archiveDefaultTitle}
						/>
					) : filtered && filteredResults === false ? (
						<EmptyState icon="search-outline" message={Translations.textNoResultsFound} />
					) : Platform.OS === 'ios' ? (
						renderArchiveItems(getEventDataMinusActiveEvents())
					) : (
						renderArchiveForAndroid(getEventDataMinusActiveEvents() || [])
					)}
				</Card>
			</ScrollView>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		flex: 1,
	},
	scroll: {
		backgroundColor: Colors.white,
		paddingTop: Dimens.padding16,
	},
	loadingIndicator: {
		flex: 1,
	},
	grid: {
		paddingTop: Platform.OS === 'android' ? Dimens.padding0 : Dimens.padding20,
		paddingBottom: Dimens.padding5,
		flexDirection: 'row',
		flexWrap: 'wrap',
		paddingLeft: Platform.OS === 'android' ? 3.5 : 17.5,
		paddingRight: Platform.OS === 'android' ? 3.5 : 17.5,
	},
});

export default ArchiveLayout;
